import type { ApplicationReceipt, ApprovalItem, IssueRun, RegistryState } from '../../data/types';
import {
  applicationPayment,
  certificatePrefix,
  crnFor,
  nextCertificateSequence,
  payableNowFor,
} from '../../domain/raise/raiseCalc';
import { money, nowStamp } from '../../lib/format';
import { newId } from '../../lib/random';
import {
  actorName,
  addApproval,
  addDocuments,
  appendLedger,
  fundCode,
  holderIdForEntity,
  today,
  withAudit,
} from '../helpers';
import { holderFromApplication } from './applicationHandlers';

function patchApplications(
  state: RegistryState,
  ids: Set<string>,
  patch: (
    application: RegistryState['applications'][number],
  ) => Partial<RegistryState['applications'][number]>,
): RegistryState {
  return {
    ...state,
    applications: state.applications.map((application) =>
      ids.has(application.id) ? { ...application, ...patch(application) } : application,
    ),
  };
}

export function issuePaymentInstruction(
  state: RegistryState,
  applicationId: string,
): RegistryState {
  const application = state.applications.find((item) => item.id === applicationId);
  const unitClass = state.classes.find((item) => item.id === application?.classId);
  if (!application) return state;
  const crn = application.paymentInstruction?.crn ?? crnFor(application.id);
  const stamp = nowStamp();
  return patchApplications(state, new Set([applicationId]), (item) => ({
    payableNow: item.payableNow ?? payableNowFor(item.amount, unitClass),
    paymentInstruction: {
      crn,
      fileName: `${fundCode(state, item.fundId)}_application_${item.reference}_payment_instruction.pdf`,
      generatedAt: stamp,
      sentAt: stamp,
      reminders: item.paymentInstruction?.reminders ?? [],
    },
  }));
}

export function regenerateInstruction(state: RegistryState, action: { id: string }): RegistryState {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  return withAudit(
    issuePaymentInstruction(state, action.id),
    'Payment instruction regenerated',
    application.reference,
    `Sent to ${application.email}`,
  );
}

export function sendPaymentReminder(state: RegistryState, action: { id: string }): RegistryState {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  const base = application.paymentInstruction ? state : issuePaymentInstruction(state, action.id);
  const next = patchApplications(base, new Set([action.id]), (item) => ({
    paymentInstruction: item.paymentInstruction && {
      ...item.paymentInstruction,
      reminders: [...item.paymentInstruction.reminders, nowStamp()],
    },
  }));
  return withAudit(
    next,
    'Payment reminder sent',
    application.reference,
    `Email to ${application.email}`,
  );
}

export function recordReceipt(
  state: RegistryState,
  action: {
    applicationId: string;
    amount: number;
    date: string;
    reference: string;
    source?: ApplicationReceipt['source'];
  },
): RegistryState {
  const application = state.applications.find((item) => item.id === action.applicationId);
  const unitClass = state.classes.find((item) => item.id === application?.classId);
  if (!application || action.amount <= 0) return state;
  const receipt: ApplicationReceipt = {
    id: newId('rc'),
    date: action.date,
    amount: action.amount,
    reference: action.reference,
    source: action.source ?? 'MANUAL',
    recordedBy: actorName(state),
  };
  const before = applicationPayment(application, unitClass);
  const existing =
    application.receipts ??
    (before.received > 0
      ? [
          {
            id: `${application.id}-opening`,
            date: application.fundsReceivedAt ?? today,
            amount: before.received,
            reference: application.reference,
            source: 'BANK_FEED' as const,
            recordedBy: 'Bank feed',
          },
        ]
      : []);
  let next = patchApplications(state, new Set([application.id]), () => ({
    receipts: [...existing, receipt],
  }));
  const updated = next.applications.find((item) => item.id === application.id)!;
  const after = applicationPayment(updated, unitClass);
  if (after.status === 'PAID') {
    next = patchApplications(next, new Set([application.id]), (item) => ({
      fundsReceivedAt: action.date,
      checklist: { ...item.checklist, funds: 'DONE' },
    }));
    next = {
      ...next,
      bankLines: next.bankLines.map((line) =>
        line.suggestedApplicationId === application.id && line.status === 'SUGGESTED'
          ? { ...line, status: 'UNMATCHED', suggestedApplicationId: undefined }
          : line,
      ),
    };
  }
  return withAudit(
    next,
    after.status === 'PAID' ? 'Application paid in full' : 'Part payment received',
    application.reference,
    `${money(action.amount, true)} received · ${money(after.outstanding, true)} outstanding`,
  );
}

export function saveIssueRun(state: RegistryState, action: { run: IssueRun }): RegistryState {
  const exists = state.issueRuns.some((run) => run.id === action.run.id);
  return {
    ...state,
    issueRuns: exists
      ? state.issueRuns.map((run) => (run.id === action.run.id ? action.run : run))
      : [action.run, ...state.issueRuns],
  };
}

export function submitIssueRun(state: RegistryState, action: { id: string }): RegistryState {
  const run = state.issueRuns.find((item) => item.id === action.id);
  if (!run || run.status !== 'DRAFT' || run.lines.length === 0) return state;
  const total = run.lines.reduce((sum, line) => sum + line.amountPaid, 0);
  let next: RegistryState = {
    ...state,
    issueRuns: state.issueRuns.map((item) =>
      item.id === run.id ? { ...item, status: 'PENDING_APPROVAL' } : item,
    ),
  };
  next = patchApplications(next, new Set(run.lines.map((line) => line.applicationId)), () => ({
    issueRunId: run.id,
  }));
  next = addApproval(next, {
    kind: 'ISSUE_RUN',
    refId: run.id,
    fundId: run.fundId,
    title: `Financial close: issue units to ${run.lines.length} applicants (${fundCode(state, run.fundId)})`,
    summary: `Settlement ${run.settlementDate} at $${run.paidPerUnit.toFixed(4)} paid per unit. Certificates ${run.certificateRelease === 'AUTO' ? 'released on issue' : 'held for manual release'}.`,
    amount: total,
  });
  return withAudit(next, 'Unit issue submitted for approval', run.reference, money(total));
}

export function applyIssueRun(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  const run = state.issueRuns.find((candidate) => candidate.id === item.refId);
  if (!run) return state;
  const runIds = new Set(run.lines.map((line) => line.applicationId));
  if (!approve) {
    const rejected = patchApplications(state, runIds, () => ({ issueRunId: undefined }));
    return {
      ...rejected,
      issueRuns: rejected.issueRuns.map((candidate) =>
        candidate.id === run.id ? { ...candidate, status: 'REJECTED' } : candidate,
      ),
    };
  }
  let next = state;
  const prefix = certificatePrefix(state, run.classId);
  let sequence = nextCertificateSequence(state, run.classId);
  const released = run.certificateRelease === 'AUTO';
  const lines = run.lines.map((line) => {
    const application = next.applications.find((candidate) => candidate.id === line.applicationId)!;
    let holderId = holderIdForEntity(next, application.investmentEntityId);
    if (!holderId) {
      const holder = holderFromApplication(next, application);
      holderId = holder.id;
      next = {
        ...next,
        holders: [...next.holders, holder],
        investorLogins: next.investorLogins.map((login) =>
          login.id === application.investorLoginId
            ? { ...login, holderIds: [...login.holderIds, holder.id] }
            : login,
        ),
      };
    }
    const certificateNo = `${prefix}-${String(sequence).padStart(6, '0')}`;
    sequence += 1;
    return { ...line, holderId, certificateNo };
  });
  next = appendLedger(
    next,
    lines.map((line) => ({
      fundId: run.fundId,
      classId: run.classId,
      holderId: line.holderId!,
      type: 'ISSUE' as const,
      effectiveDate: run.settlementDate,
      units: line.units,
      amount: line.amountPaid,
      unitPrice: run.paidPerUnit,
      reference: run.reference,
      eventId: run.id,
      memo: `Units issued at financial close · certificate ${line.certificateNo}`,
      postedBy: run.createdBy,
      approvedBy: approver,
    })),
  );
  if (run.paidPerUnit < run.issuePrice) {
    next = {
      ...next,
      commitments: [
        ...next.commitments,
        ...lines.map((line) => ({
          id: newId('cm'),
          holderId: line.holderId!,
          fundId: run.fundId,
          classId: run.classId,
          amount: Math.round(line.units * run.issuePrice * 100) / 100,
          date: run.settlementDate,
        })),
      ],
    };
  }
  next = patchApplications(next, runIds, () => ({ status: 'ISSUED' }));
  next = addDocuments(
    next,
    lines.flatMap((line) => [
      {
        holderId: line.holderId,
        fundId: run.fundId,
        kind: 'UNIT_CERTIFICATE' as const,
        name: `${fundCode(state, run.fundId)} unit certificate ${line.certificateNo}`,
        date: run.settlementDate,
        sizeKb: 96,
        visibleToInvestor: released,
        refId: run.id,
      },
      {
        holderId: line.holderId,
        fundId: run.fundId,
        kind: 'TRANSACTION_CONFIRMATION' as const,
        name: `${fundCode(state, run.fundId)} unit issue confirmation (${run.reference})`,
        date: run.settlementDate,
        sizeKb: 72,
        visibleToInvestor: true,
        refId: run.id,
      },
    ]),
  );
  next = {
    ...next,
    issueRuns: next.issueRuns.map((candidate) =>
      candidate.id === run.id
        ? {
            ...candidate,
            lines,
            status: 'ISSUED',
            approvedBy: approver,
            issuedAt: nowStamp(),
            certificatesReleasedAt: released ? nowStamp() : undefined,
          }
        : candidate,
    ),
  };
  if (run.closeOffer) {
    next = {
      ...next,
      offers: next.offers.map((offer) =>
        offer.id === run.offerId ? { ...offer, status: 'CLOSED' } : offer,
      ),
      classes: next.classes.map((unitClass) =>
        unitClass.id === run.classId ? { ...unitClass, open: false } : unitClass,
      ),
      funds: next.funds.map((fund) =>
        fund.id === run.fundId && fund.status === 'RAISING' ? { ...fund, status: 'CLOSED' } : fund,
      ),
    };
  }
  return next;
}

export function releaseCertificates(state: RegistryState, action: { id: string }): RegistryState {
  const run = state.issueRuns.find((item) => item.id === action.id);
  if (!run || run.status !== 'ISSUED' || run.certificatesReleasedAt) return state;
  const next: RegistryState = {
    ...state,
    documents: state.documents.map((doc) =>
      doc.refId === run.id && doc.kind === 'UNIT_CERTIFICATE'
        ? { ...doc, visibleToInvestor: true }
        : doc,
    ),
    issueRuns: state.issueRuns.map((item) =>
      item.id === run.id ? { ...item, certificatesReleasedAt: nowStamp() } : item,
    ),
  };
  return withAudit(
    next,
    'Unit certificates released',
    run.reference,
    `${run.lines.length} certificates`,
  );
}
